import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { usePayOutstandingOrder } from "@/features/Packages/hooks/usePayOutstandingOrder";
import { CheckBox, Link } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { PackageOrderFooterQuery } from "./__generated__/PackageOrderFooterQuery.graphql";

type Props = {
  orderId: string;
  packageId: string;
  onBack?: () => void;
};

export const PackageOrderFooter = ({ orderId, packageId, onBack }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<PackageOrderFooterQuery>(
    PackageOrderFooterQueryString
  );

  useEffect(() => {
    if (orderId && packageId) {
      loadQuery({
        memberPackageId: packageId,
        orderId,
      });
    }
  }, [loadQuery, orderId, packageId]);

  return (
    <Suspense>
      {queryRef ? (
        <PackageOrderFooterInner queryRef={queryRef} onBack={onBack} />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<PackageOrderFooterQuery>;
  onBack?: () => void;
};
export const PackageOrderFooterInner = ({ queryRef, onBack }: InnerProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const { showToast } = useToast();
  const [termsChecked, setTermsChecked] = useState(false);

  const data = usePreloadedQuery(PackageOrderFooterQueryString, queryRef);

  const order = data.order;
  const orderId = order?.id || "";
  const memberPackage = data.memberPackage;

  const termsAndConditionsUrl =
    memberPackage?.package?.displayInApp?.termsAndConditionsUrl;

  const squareCardId = memberPackage?.squareCard?.id || null;
  const total = order?.total || 0;

  const [handlePayOrder, isPaying] = usePayOutstandingOrder();

  const onConfirmAndPay = useCallback(() => {
    if (order && squareCardId) {
      handlePayOrder(
        {
          orderId,
          paymentSourceId: squareCardId,
        },
        () => {
          showToast({
            description: t`Payment Successful!`,
            semanticStyle: "success",
            toastId: "package-payment-success",
          });
          onBack?.();
        },
        (error) => {
          showToast({
            description:
              error.message ||
              t`Unable to process payment. Please try a different card or reach out to the club for support.`,
            semanticStyle: "error",
            toastId: "package-payment-error",
          });
        }
      );
    }
  }, [handlePayOrder, onBack, order, orderId, showToast, squareCardId]);

  const isPayButtonDisabled =
    (!termsChecked && !!termsAndConditionsUrl) || !squareCardId;

  return (
    <VStack p={6} gap={4} borderTopColor="surface-dividers" borderTopWidth={1}>
      {termsAndConditionsUrl ? (
        <HStack gap={1} alignItems="center" alignSelf="center">
          <CheckBox
            isChecked={termsChecked}
            onPress={() => setTermsChecked(!termsChecked)}
          />
          <Text>{t`I have read and agree to`}</Text>
          <Link
            href={termsAndConditionsUrl}
            target="_blank"
          >{t`Terms and Conditions`}</Link>
        </HStack>
      ) : null}
      <Button
        accessibilityLabel={t`Confirm and Pay ${currencySymbol}${ConvertCentsToDollars(
          total
        )}`}
        size="small"
        isDisabled={isPayButtonDisabled}
        onPress={onConfirmAndPay}
        isLoading={isPaying}
      >{t`Confirm and Pay ${currencySymbol}${ConvertCentsToDollars(
        total
      )}`}</Button>
    </VStack>
  );
};

const PackageOrderFooterQueryString = graphql`
  query PackageOrderFooterQuery($memberPackageId: ID!, $orderId: ID!) {
    memberPackage: node(id: $memberPackageId) {
      ... on MemberPackage {
        squareCard {
          id
        }
        package {
          displayInApp {
            termsAndConditionsUrl
          }
        }
      }
    }
    order: node(id: $orderId) {
      ... on Order {
        id
        total
      }
    }
  }
`;
